import type { CSSProperties, ReactNode, Ref } from "react";
import { pluralize } from "@common/helpers/string";
import { Button } from "@design-system/Button/Button";
import { ChevronRightSmallIcon, CollapseIcon, ExpandIcon } from "@icons";
import styles from "./Board.module.scss";
import laneStyles from "./BoardLanes.module.scss";

interface BoardCollapseToggleProps {
    /** What the toggle collapses, e.g. "Prep". */
    name: string;
    isExpanded: boolean;
    onToggle: () => void;
}

/** Lane disclosure chevron: points down while expanded, right while collapsed. */
export const BoardCollapseToggle = ({
    name,
    isExpanded,
    onToggle,
}: Readonly<BoardCollapseToggleProps>) => (
    <Button
        variant="secondary"
        size="sm"
        iconOnly
        aria-label={isExpanded ? `Collapse ${name}` : `Expand ${name}`}
        aria-expanded={isExpanded}
        className={laneStyles.collapseToggle}
        onClick={onToggle}
    >
        <ChevronRightSmallIcon aria-hidden className={laneStyles.chevron} />
    </Button>
);

/** Column toggle: arrows pointing in to narrow the column, out to widen it. */
export const BoardColumnToggle = ({
    name,
    isExpanded,
    onToggle,
}: Readonly<BoardCollapseToggleProps>) => (
    <Button
        variant="secondary"
        size="sm"
        iconOnly
        aria-label={isExpanded ? `Collapse ${name}` : `Expand ${name}`}
        aria-expanded={isExpanded}
        className={laneStyles.collapseToggle}
        onClick={onToggle}
    >
        {isExpanded ? (
            <CollapseIcon aria-hidden className={laneStyles.horizontalIcon} />
        ) : (
            <ExpandIcon aria-hidden className={laneStyles.horizontalIcon} />
        )}
    </Button>
);

/** A collapsed column's expand toggle in the sticky header row of a board with lanes. */
export const BoardCollapsedColumnHeader = ({
    toggle,
}: Readonly<{ toggle: ReactNode }>) => (
    <div className={laneStyles.collapsedColumnHeader}>{toggle}</div>
);

export interface BoardCollapsedColumnProps {
    title: string;
    titleId: string;
    count: number;
    /** The expanded header's icon, upright above the vertical title. */
    icon?: ReactNode;
    /**
     * Expand toggle; absent when the surface doesn't allow collapsing, or
     * when the header row holds it (boards with lanes).
     */
    toggle: ReactNode;
    /** Grid placement from the layout. */
    style: CSSProperties;
    /** The dragged card is over the strip. */
    isDropTarget?: boolean;
    /** Dims the strip; the drag announcement reads the reason. */
    refusalReason?: string;
    ref?: Ref<HTMLElement>;
}

/** A collapsed column: a narrow strip with its icon, vertical title and count. */
export const BoardCollapsedColumn = ({
    title,
    titleId,
    count,
    icon,
    toggle,
    style,
    isDropTarget = false,
    refusalReason,
    ref,
}: Readonly<BoardCollapsedColumnProps>) => (
    <section
        ref={ref}
        aria-labelledby={titleId}
        className={laneStyles.collapsedColumn}
        style={style}
        data-drop-target={isDropTarget}
        data-refuses-drop={refusalReason !== undefined}
    >
        <div className={laneStyles.collapsedColumnContent}>
            {Boolean(toggle) && (
                <div className={laneStyles.collapsedColumnToggleRow}>
                    {toggle}
                </div>
            )}
            <h3 className={laneStyles.collapsedColumnTitle}>
                {Boolean(icon) && (
                    <span aria-hidden className={styles.headerIcon}>
                        {icon}
                    </span>
                )}
                <span id={titleId}>{title}</span>
                <span className={laneStyles.collapsedColumnCount}>
                    <span className={laneStyles.visuallyHidden}>, </span>
                    {count}
                    <span className={laneStyles.visuallyHidden}>
                        {` ${pluralize("card", count)}, collapsed`}
                    </span>
                </span>
            </h3>
        </div>
    </section>
);
